'use client'

import { useEffect, useRef, useState } from 'react'
import { FileImageIcon } from 'lucide-react'
import { useTranslations } from 'next-intl'

import { cn } from '@/lib/utils'

const IMAGE_RETRIES = 3

/** Retry the short gap between upload and message linking without changing
 * signed URLs. Callers key by src so a new attachment starts fresh. */
export function AttachmentImage({
  src,
  alt,
  className,
  compact = false,
}: {
  src: string
  alt: string
  className?: string
  compact?: boolean
}) {
  const t = useTranslations('Chat.artifact')
  const tPreview = useTranslations('FilePreview.deferred')
  const [attempt, setAttempt] = useState(0)
  const [failed, setFailed] = useState(false)
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)
  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current)
    },
    [],
  )

  if (failed) {
    return (
      <span
        role="status"
        aria-label={t('cannotLoadPreview')}
        className={cn(
          'text-muted-foreground flex size-full flex-col items-center justify-center text-center text-xs',
          !compact && 'min-h-24 gap-2 p-3',
        )}
      >
        <FileImageIcon className={compact ? 'size-3' : 'size-6'} />
        {!compact && <span>{t('cannotLoadPreview')}</span>}
        {!compact && <span>{tPreview('openToPreview')}</span>}
      </span>
    )
  }

  return (
    <img
      key={attempt}
      alt={alt}
      className={cn('size-full object-contain', className)}
      src={src}
      loading="lazy"
      onError={() => {
        if (attempt >= IMAGE_RETRIES || /^(blob|data):/.test(src)) {
          setFailed(true)
          return
        }
        if (timer.current) clearTimeout(timer.current)
        timer.current = setTimeout(
          () => setAttempt(value => value + 1),
          1000 * 2 ** attempt,
        )
      }}
    />
  )
}
